import type { BetRecommendation, DecisionStatus } from '../types';

/**
 * Priority ordering for DecisionStatus values.
 * Lower number = higher priority (i.e., the "best" recommendation to surface).
 *
 * Priority hierarchy (per issue spec):
 *   STRONG_BET   → 0  (always surface over everything)
 *   QUALIFIED_BET → 1
 *   LEAN          → 2
 *   NO_BET        → 3
 *   INVALID       → 4  (show BLOCKED only when nothing better exists)
 */
export const DECISION_STATUS_PRIORITY: Record<DecisionStatus, number> = {
  STRONG_BET: 0,
  QUALIFIED_BET: 1,
  LEAN: 2,
  NO_BET: 3,
  INVALID: 4,
};

/**
 * Returns the highest-priority recommendation from a list for a given game.
 *
 * Selection rule:
 *   1. Status priority (STRONG_BET > QUALIFIED_BET > LEAN > NO_BET > INVALID)
 *   2. Edge magnitude (higher is better), used as a tiebreaker within the same status
 *
 * This ensures a QUALIFIED_BET with lower edge always beats an INVALID recommendation
 * with higher edge, preventing the GameCard from displaying BLOCKED when a qualified
 * recommendation exists for the same game.
 *
 * @param gamePk - The MLB game primary key to filter by
 * @param recommendations - Full list of recommendations to search
 * @returns The best recommendation for the game, or undefined if none exist
 */
export function getBestGameRecommendation(
  gamePk: number,
  recommendations: BetRecommendation[],
): BetRecommendation | undefined {
  const gameRecs = recommendations.filter((r) => r.gamePk === gamePk);
  if (gameRecs.length === 0) return undefined;

  return gameRecs.reduce((best, rec) => {
    const bestPriority = DECISION_STATUS_PRIORITY[best.decisionStatus] ?? 99;
    const recPriority = DECISION_STATUS_PRIORITY[rec.decisionStatus] ?? 99;

    if (recPriority < bestPriority) return rec;
    if (recPriority === bestPriority && rec.edge > best.edge) return rec;
    return best;
  });
}

/**
 * Sorts a list of recommendations by priority (best first).
 * Useful for displaying ordered lists in the Betting Advisor and Edge Board.
 */
export function sortRecommendationsByPriority(
  recommendations: BetRecommendation[],
): BetRecommendation[] {
  return [...recommendations].sort((a, b) => {
    const aPriority = DECISION_STATUS_PRIORITY[a.decisionStatus] ?? 99;
    const bPriority = DECISION_STATUS_PRIORITY[b.decisionStatus] ?? 99;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return b.edge - a.edge;
  });
}
